import { motion } from "framer-motion";
import { Leaf, PiggyBank, HeartHandshake, Zap } from "lucide-react";

const ADV = [
    {
        icon: PiggyBank,
        kicker: "Économie",
        title: "Jusqu'à -70% sur vos trajets",
        desc: "Partagez le trajet à plusieurs, même en van jusqu'à 6 personnes, pour rendre chaque course beaucoup plus accessible.",
        accent: "from-volt/15 to-transparent",
    },
    {
        icon: Leaf,
        kicker: "Écologie",
        title: "Moins de voitures, moins de CO₂",
        desc: "Chaque trajet partagé réduit l'empreinte carbone. Un geste simple, un impact réel.",
        accent: "from-emerald-500/10 to-transparent",
    },
    {
        icon: HeartHandshake,
        kicker: "Communauté",
        title: "Voyagez ensemble",
        desc: "Étudiants, voyageurs, groupes ou collègues — Coway facilite les trajets partagés entre personnes allant dans la même direction.",
        accent: "from-pink-500/10 to-transparent",
    },
    {
        icon: Zap,
        kicker: "Simplicité",
        title: "Matching en moins de 3 min",
        desc: "Notre algorithme trouve les passagers compatibles instantanément, 24h/24.",
        accent: "from-blue-500/10 to-transparent",
    },
];

export default function Advantages() {
    return (
        <section
            id="advantages"
            data-testid="advantages-section"
            className="py-24 lg:py-32"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
                    <div className="max-w-2xl">
                        <p className="text-xs text-coway uppercase tracking-[0.3em] font-semibold mb-5">
                            Avantages
                        </p>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
                            Pourquoi tout le
                            <br />
                            monde y gagne.
                        </h2>
                    </div>
                    <p className="text-lg text-slate-600 max-w-md">
                        Économie, écologie, rencontres, simplicité — Coway
                        transforme le taxi traditionnel en expérience moderne et
                        partagée.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                    {ADV.map((a, i) => (
                        <motion.div
                            key={a.title}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{
                                duration: 0.7,
                                delay: i * 0.08,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            whileHover={{ y: -6 }}
                            data-testid={`advantage-card-${i + 1}`}
                            className="relative p-8 lg:p-10 rounded-3xl bg-white border border-slate-200 hover:border-coway/40 transition-colors card-elevated overflow-hidden group"
                        >
                            <div
                                className={`absolute -top-32 -right-32 w-80 h-80 rounded-full blur-3xl opacity-60 group-hover:opacity-100 transition-opacity duration-700`}
                                style={{
                                    background:
                                        a.kicker === "Économie"
                                            ? "radial-gradient(circle, rgba(81,199,173,0.18), transparent 60%)"
                                            : a.kicker === "Écologie"
                                              ? "radial-gradient(circle, rgba(94,191,143,0.16), transparent 60%)"
                                              : a.kicker === "Communauté"
                                                ? "radial-gradient(circle, rgba(244,114,182,0.10), transparent 60%)"
                                                : "radial-gradient(circle, rgba(96,165,250,0.12), transparent 60%)",
                                }}
                            />
                            <div className="relative z-10">
                                <div className="flex items-center gap-3 mb-6">
                                    <span className="inline-flex w-11 h-11 rounded-2xl bg-coway-soft border border-coway/15 items-center justify-center">
                                        <a.icon
                                            className="w-5 h-5 text-coway"
                                            strokeWidth={1.7}
                                        />
                                    </span>
                                    <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                                        {a.kicker}
                                    </span>
                                </div>
                                <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 max-w-md">
                                    {a.title}
                                </h3>
                                <p className="mt-3 text-base text-slate-500 max-w-md leading-relaxed">
                                    {a.desc}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
